import * as Yup from 'yup';
import React, { useContext, useEffect, useState } from 'react';
import { Row, Col, Modal as BootstrapModal } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Button, Form, FormInput } from 'modules';
import { AgreementByContractQuery } from 'queries/Agreement/AgreementData';
import Agreement from './Agreement';
import { notifySuccess } from 'helpers/notify';
import { WebsocketActions, WebsocketContext } from 'services/WebsocketContext';
import DruhPrijmuSelect from 'components/DruhPrijmuSelect';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import {
  selectClientAccess,
  selectConsultantAccess,
  selectOnlineForm,
  selectOnlineFormContract,
} from 'stores/onlineForm/onlineForm';
import { UpdateClientMutation } from 'queries/Client';

const myUUID = uuidv4();

interface FormModalProps {
  onClose: () => void;
}

const FormModal = ({ onClose }: FormModalProps) => {
  const wsContext = useContext(WebsocketContext);

  const form = useSelector(selectOnlineForm);
  const contract = useSelector(selectOnlineFormContract);
  const isConsultantAccess = useSelector(selectConsultantAccess);
  const isClientAccess = useSelector(selectClientAccess);

  const [updateClientData] = useMutation(UpdateClientMutation);

  const [loading, setLoading] = useState(false);

  // souhlas partnera smlouvy
  const agreementQuery = useQuery(AgreementByContractQuery, {
    variables: {
      where: {
        contractId: contract?.id,
        clientId: contract?.id_klient_2,
      },
    },
  });

  const agreementData = agreementQuery.data?.agreementByContract;
  const agreementLoading = agreementQuery.loading;

  const handleClose = (e: React.MouseEvent) => {
    onClose();
    e.stopPropagation();
    e.preventDefault();
  };

  const validation = Yup.object().shape({
    lastName: Yup.string().required('Příjmení je povinné!'),
    firstName: Yup.string().required('Jméno je povinné!'),
    cislo_op: Yup.string().required('Číslo OP je povinné!'),
    rodne_cislo: Yup.string().required('Rodné číslo je povinné!'),
    // adresa
    street: Yup.string().nullable().required('Ulica je povinná!'),
    houseNumber: Yup.string().nullable().required('Súpisné číslo je povinné!'),
    city: Yup.string().nullable().required('Miesto je povinné!'),
    zipCode: Yup.string().nullable().required('PSČ je povinné!'),
    // income
    income: Yup.string().nullable().required('Druh príjmu je povinný!'),
    // souhlas
    agreement: Yup.string().nullable().required('Súhlas je povinný!'),
  });

  const formik = useFormik<GQL.ITClient2>({
    initialValues: {
      lastName: contract?.client2?.prijmeni,
      firstName: contract?.client2?.jmeno,
      mobil: contract?.client2?.mobil,
      email: contract?.client2?.email,
      cislo_op: contract?.client2?.cislo_op,
      rodne_cislo: contract?.client2?.rodne_cislo,
      // adresa
      street: contract?.client2?.trvale_ulice,
      houseNumber: contract?.client2?.trvale_cislo_popisne,
      city: contract?.client2?.trvale_mesto,
      zipCode: contract?.client2?.trvale_psc,
      // koresp. adresa
      mailingAddress: !!contract?.client2?.koresp_mesto,
      mailingAddressStreet: contract?.client2?.koresp_ulice,
      mailingAddressHouseNumber: contract?.client2?.koresp_cislo_popisne,
      mailingAddressCity: contract?.client2?.koresp_mesto,
      mailingAddressZipCode: contract?.client2?.koresp_psc,
      // income
      income: contract?.client2?.prijem,
      incomeValue: contract?.client2?.prijem_vyse,
    } as any,
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if (formik.isValid) {
        setLoading(true);
        // uložení dat klienta
        updateClientData({
          variables: {
            id: contract?.client2?.id,
            data: {
              prijmeni: values.lastName,
              jmeno: values.firstName,
              mobil: values.mobil,
              email: values.email,
              cislo_op: values.cislo_op,
              rodne_cislo: values.rodne_cislo,
              // adresa
              trvale_ulice: values.street,
              trvale_cislo_popisne: values.houseNumber,
              trvale_mesto: values.city,
              trvale_psc: values.zipCode,
              // koresp. adresa
              koresp_ulice: values.mailingAddressStreet,
              koresp_cislo_popisne: values.mailingAddressHouseNumber,
              koresp_mesto: values.mailingAddressCity,
              koresp_psc: values.mailingAddressZipCode,
              // income
              prijem: values.income,
              prijem_vyse: values.incomeValue,
            },
          },
        })
          .then(() => {
            // aktualizace smlouvy
            wsContext?.send({
              action: WebsocketActions.REFRESH_CONTRACT_STATE,
            });
            // notifikace
            notifySuccess('Manžel / manželka bol pridaný k zmluve');
            // zavření
            onClose();
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
  });

  // změna v souhlasu, provedl klient
  const onChangeAgreement = (e: React.ChangeEvent) => {
    const { value } = e.target as HTMLInputElement;
    formik.handleChange(e);
    wsContext?.send({
      action: WebsocketActions.CONTRACT_PARTNER_CHANGE,
      value: {
        name: 'agreement',
        value,
      },
    });
  };

  const handleChange = (e: React.ChangeEvent) => {
    const { name, value } = e.target as HTMLInputElement;
    formik.handleChange(e);
    wsContext?.send({
      action: WebsocketActions.CONTRACT_PARTNER_CHANGE,
      value: {
        sender: myUUID,
        name,
        value,
      },
    });
  };

  const onSubmitAgreement = () => {
    wsContext?.send({
      action: WebsocketActions.CONTRACT_PARTNER_CHANGE,
      value: {
        name: 'refetchAgreement',
      },
    });
  };

  useEffect(() => {
    if (!formik.values.agreement && !!agreementData) {
      formik.setFieldValue('agreement', true);
    }
    // eslint-disable-next-line
  }, [agreementData]);

  useEffect(() => {
    wsContext?.ws?.on(WebsocketActions.CONTRACT_PARTNER_CHANGE, (data) => {
      // změna dat partnera
      if (data.sender !== myUUID) {
        if (data.name in formik.values && (formik.values as any)[data.name] !== data.value) {
          formik.setFieldValue(data.name, data.value);
        }
      }
      if (data.name === 'refetchAgreement') {
        agreementQuery.refetch();
      }
    });
    return () => {
      wsContext?.ws?.off(WebsocketActions.CONTRACT_PARTNER_CHANGE);
    };
    // eslint-disable-next-line
  }, [wsContext]);

  if (!contract || !form) {
    return null;
  }

  return (
    <BootstrapModal
      show
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      animation={false}
      dialogClassName="modal-80w max-w-800"
    >
      <Form onSubmit={formik.handleSubmit}>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Manžel / Manželka</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <Row>
            <Col sm={6}>
              <FormInput
                name="lastName"
                label="Příjmení"
                required
                value={formik.values.lastName}
                error={formik.errors.lastName}
                onChange={handleChange}
              />
            </Col>
            <Col sm={6}>
              <FormInput
                name="firstName"
                label="Jméno"
                required
                value={formik.values.firstName}
                error={formik.errors.firstName}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={3}>
              <FormInput
                name="mobil"
                label="Mobil"
                required
                value={formik.values.mobil}
                error={formik.errors.mobil}
                onChange={handleChange}
              />
            </Col>
            <Col sm={3}>
              <FormInput
                name="email"
                label="E-mail"
                required
                value={formik.values.email}
                error={formik.errors.email}
                onChange={handleChange}
              />
            </Col>
            <Col sm={3}>
              <FormInput
                name="cislo_op"
                label="Číslo OP"
                required
                value={formik.values.cislo_op}
                error={formik.errors.cislo_op}
                onChange={handleChange}
              />
            </Col>
            <Col sm={3}>
              <FormInput
                name="rodne_cislo"
                label="Rodné číslo"
                required
                value={formik.values.rodne_cislo}
                error={formik.errors.rodne_cislo}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <Row>
            <Col md={4} xs={8}>
              <FormInput
                name="street"
                label={'Ulica'}
                required
                value={formik.values.street}
                error={formik.errors.street}
                onChange={handleChange}
              />
            </Col>
            <Col md={2} xs={4}>
              <FormInput
                name="houseNumber"
                label={'Súpisné č.'}
                required
                value={formik.values.houseNumber}
                error={formik.errors.houseNumber}
                onChange={handleChange}
              />
            </Col>
            <Col md={4} xs={8}>
              <FormInput
                name="city"
                label={'Mesto'}
                required
                value={formik.values.city}
                error={formik.errors.city}
                onChange={handleChange}
              />
            </Col>
            <Col md={2} xs={4}>
              <FormInput
                name="zipCode"
                label={'PSČ'}
                required
                value={formik.values.zipCode}
                error={formik.errors.zipCode}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <FormInput
                type="checkbox"
                name="mailingAddress"
                label={'Korešpondenčná adresa'}
                checked={!!formik.values.mailingAddress}
                error={formik.errors.mailingAddress}
                onChange={handleChange}
              />
            </Col>
          </Row>

          {formik.values.mailingAddress === true && (
            <Row>
              <Col md={4} xs={8}>
                <FormInput
                  name="mailingAddressStreet"
                  label={'Koresp. ulica'}
                  value={formik.values.mailingAddressStreet}
                  error={formik.errors.mailingAddressStreet}
                  onChange={handleChange}
                />
              </Col>
              <Col md={2} xs={4}>
                <FormInput
                  name="mailingAddressHouseNumber"
                  label={'Koresp. súpisné č.'}
                  value={formik.values.mailingAddressHouseNumber}
                  error={formik.errors.mailingAddressHouseNumber}
                  onChange={handleChange}
                />
              </Col>
              <Col md={4} xs={8}>
                <FormInput
                  name="mailingAddressCity"
                  label={'Koresp. mesto'}
                  value={formik.values.mailingAddressCity}
                  error={formik.errors.mailingAddressCity}
                  onChange={handleChange}
                />
              </Col>
              <Col md={2} xs={4}>
                <FormInput
                  name="mailingAddressZipCode"
                  label={'Koresp. PSČ'}
                  value={formik.values.mailingAddressZipCode}
                  error={formik.errors.mailingAddressZipCode}
                  onChange={handleChange}
                />
              </Col>
            </Row>
          )}

          <Row>
            <Col sm={5}>
              <DruhPrijmuSelect
                name="income"
                label={'Príjem'}
                required
                value={formik.values?.income}
                error={formik.errors?.income}
                onChange={handleChange}
              />
            </Col>
            <Col sm={3}>
              <FormInput
                type="number"
                name="incomeValue"
                label={'Výška príjmu'}
                min={0}
                value={formik.values?.incomeValue}
                error={formik.errors?.incomeValue}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <hr />
          <Agreement
            isViewMode={isClientAccess}
            contract={contract}
            form={form}
            loading={agreementLoading}
            data={agreementData}
            values={formik.values}
            errors={formik.errors}
            onChange={onChangeAgreement}
            onSubmit={onSubmitAgreement}
          />
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Zavrieť
          </Button>
          <Button loading={loading} variant="primary" type="submit" disabled={!isConsultantAccess}>
            Uložiť
          </Button>
        </BootstrapModal.Footer>
      </Form>
    </BootstrapModal>
  );
};

export default FormModal;
